import { api } from './api';

/**
 * WEB PUSH ABONELİĞİ.
 *
 * ⚠️ iOS'ta push YALNIZ ANA EKRANA EKLENMİŞ uygulamada çalışır (iOS 16.4+).
 *    Safari sekmesinde `Notification` API'si vardır ama abonelik başarısız olur.
 *    Bu yüzden kullanıcıya "izin ver" düğmesi göstermeden önce kurulu olup
 *    olmadığına bakıyoruz — çalışmayacak bir düğme, hiç düğme olmamasından kötü.
 *
 * ⚠️ İZİN İSTEĞİ KULLANICI HAREKETİNDEN GELMELİ. Sayfa açılışında istemek
 *    iOS'ta sessizce reddedilir, Chrome'da ise kullanıcıyı "engelle"ye iter ve
 *    o karar KALICIDIR.
 */

export type PushDurum =
  | 'destekyok' // tarayıcı push bilmiyor
  | 'kurulumgerek' // iOS: önce ana ekrana eklenmeli
  | 'kapali' // izin verilmemiş
  | 'reddedildi' // kullanıcı engellemiş — geri almak ayarlardan
  | 'acik';

/** iOS Safari mi (iPhone/iPad)? */
function iosMu(): boolean {
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    // iPadOS masaüstü Safari gibi davranır; dokunmatik + Mac = iPad.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Ana ekrandan mı açıldı (standalone)? */
export function kuruluMu(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export async function pushDurumu(): Promise<PushDurum> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'destekyok';
  }
  // iOS'ta kurulu değilken abonelik denemesi hata verir; kullanıcıya doğru
  // yönlendirmeyi göster.
  if (iosMu() && !kuruluMu()) return 'kurulumgerek';

  if (Notification.permission === 'denied') return 'reddedildi';
  if (Notification.permission !== 'granted') return 'kapali';

  const reg = await navigator.serviceWorker.ready;
  const abone = await reg.pushManager.getSubscription();
  return abone ? 'acik' : 'kapali';
}

function base64ToUint8(b64: string): Uint8Array {
  const dolgu = '='.repeat((4 - (b64.length % 4)) % 4);
  const temiz = (b64 + dolgu).replace(/-/g, '+').replace(/_/g, '/');
  const ham = atob(temiz);
  return Uint8Array.from([...ham].map((c) => c.charCodeAt(0)));
}

/** Kullanıcı hareketinden ÇAĞRILMALI. */
export async function pushAc(): Promise<PushDurum> {
  const { acikAnahtar } = await api.get<{ acikAnahtar: string | null }>('/push/anahtar');
  if (!acikAnahtar) return 'destekyok';

  const izin = await Notification.requestPermission();
  if (izin !== 'granted') return izin === 'denied' ? 'reddedildi' : 'kapali';

  const reg = await navigator.serviceWorker.ready;
  const abone =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      // Sessiz push YASAK: her push bir bildirim göstermeli.
      userVisibleOnly: true,
      applicationServerKey: base64ToUint8(acikAnahtar) as BufferSource,
    }));

  const j = abone.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
  await api.post('/push/abone', {
    endpoint: j.endpoint,
    keys: j.keys,
    cihaz: navigator.userAgent.slice(0, 200),
  });
  return 'acik';
}

export async function pushKapat(): Promise<PushDurum> {
  const reg = await navigator.serviceWorker.ready;
  const abone = await reg.pushManager.getSubscription();
  if (abone) {
    await api
      .post('/push/abone-sil', { endpoint: abone.endpoint })
      .catch(() => undefined);
    await abone.unsubscribe();
  }
  return 'kapali';
}
